/**
 * API Connectors UI
 * Manage external service connections
 */

import { useState, useEffect } from 'react';
import { getConnectorManager, CONNECTOR_TYPES, CONNECTOR_CATEGORIES } from '../lib/connectors';

export function Connectors() {
  const [connectorManager] = useState(() => getConnectorManager());
  const [connections, setConnections] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = () => {
    const active = connectorManager.getAllConnections();
    setConnections(active);
  };

  const availableConnectors = Object.values(CONNECTOR_TYPES).filter(
    connector => !connectorManager.isConnected(connector.id)
  );

  const filteredConnectors = selectedCategory === 'all'
    ? availableConnectors
    : availableConnectors.filter(c => c.category === selectedCategory);

  const handleConnect = (connector) => {
    setSelectedConnector(connector);
    setCredentials({});
    setShowAddModal(true);
  };

  const handleSave = async () => {
    try {
      connectorManager.addConnection(selectedConnector.id, credentials);

      // Test connection
      const result = await connectorManager.testConnection(selectedConnector.id);
      setTestResults({
        [selectedConnector.id]: result
      });

      loadConnections();
      setShowAddModal(false);
      setSelectedConnector(null);
      setCredentials({});
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleDisconnect = (connectorId) => {
    if (confirm(`Disconnect from ${CONNECTOR_TYPES[connectorId.toUpperCase()].name}?`)) {
      connectorManager.removeConnection(connectorId);
      loadConnections();
    }
  };

  const handleTest = async (connectorId) => {
    const result = await connectorManager.testConnection(connectorId);
    setTestResults({ ...testResults, [connectorId]: result });
  };

  return (
    <div className="connectors">
      <div className="connectors-header">
        <h2>🔌 API Connectors</h2>
        <p className="subtitle">{connections.length} connected • {availableConnectors.length} available</p>
      </div>

      {/* Active Connections */}
      {connections.length > 0 && (
        <div className="active-connections">
          <h3>Active Connections</h3>
          <div className="connector-grid">
            {connections.map(conn => (
              <div key={conn.id} className="connector-card active">
                <div className="connector-icon">{conn.icon}</div>
                <div className="connector-info">
                  <div className="connector-name">{conn.name}</div>
                  <div className="connector-category">{CONNECTOR_CATEGORIES[conn.category].icon} {CONNECTOR_CATEGORIES[conn.category].name}</div>
                  <div className="connector-status">
                    <span className="status-dot connected"></span>
                    Connected
                  </div>
                </div>
                <div className="connector-actions">
                  <button onClick={() => handleTest(conn.id)} className="btn-secondary btn-sm">
                    Test
                  </button>
                  <button onClick={() => handleDisconnect(conn.id)} className="btn-danger btn-sm">
                    Disconnect
                  </button>
                </div>

                {testResults[conn.id] && (
                  <div className={`test-result ${testResults[conn.id].success ? 'success' : 'error'}`}>
                    {testResults[conn.id].success ? '✅' : '❌'}
                    {testResults[conn.id].message || testResults[conn.id].error || 'Connection verified'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Connectors */}
      <div className="available-connectors">
        <div className="section-header">
          <h3>Available Connectors</h3>

          <div className="category-filter">
            <button
              className={selectedCategory === 'all' ? 'active' : ''}
              onClick={() => setSelectedCategory('all')}
            >
              All ({availableConnectors.length})
            </button>
            {Object.entries(CONNECTOR_CATEGORIES).map(([key, cat]) => {
              const count = availableConnectors.filter(c => c.category === key).length;
              return (
                <button
                  key={key}
                  className={selectedCategory === key ? 'active' : ''}
                  onClick={() => setSelectedCategory(key)}
                >
                  {cat.icon} {cat.name} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="connector-grid">
          {filteredConnectors.map(connector => (
            <div key={connector.id} className="connector-card">
              <div className="connector-icon">{connector.icon}</div>
              <div className="connector-info">
                <div className="connector-name">{connector.name}</div>
                <div className="connector-description">{connector.description}</div>
                <div className="connector-category">{CONNECTOR_CATEGORIES[connector.category].icon} {CONNECTOR_CATEGORIES[connector.category].name}</div>
              </div>
              <button onClick={() => handleConnect(connector)} className="btn-primary btn-sm">
                Connect
              </button>
            </div>
          ))}

          {filteredConnectors.length === 0 && (
            <div className="no-connectors">
              <p>All {CONNECTOR_CATEGORIES[selectedCategory]?.name || 'connectors'} are connected!</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Connection Modal */}
      {showAddModal && selectedConnector && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedConnector.icon} Connect to {selectedConnector.name}</h3>
              <button onClick={() => setShowAddModal(false)} className="close-btn">×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">{selectedConnector.description}</p>

              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                {selectedConnector.requiredFields.map(field => (
                  <div key={field} className="form-group">
                    <label>
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      <span className="required">*</span>
                    </label>
                    <input
                      type={field.includes('token') || field.includes('key') || field.includes('secret') ? 'password' : 'text'}
                      value={credentials[field] || ''}
                      onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
                      placeholder={`Enter ${field}`}
                      required
                    />
                  </div>
                ))}

                {selectedConnector.optionalFields?.map(field => (
                  <div key={field} className="form-group">
                    <label>
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      <span className="optional">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={credentials[field] || ''}
                      onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
                      placeholder={`Enter ${field}`}
                    />
                  </div>
                ))}

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Connect
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Connectors;
